import { incompleteAction } from './actionReadiness';
// client/src/pages/actions/EditAction.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../api/api";
import { useOrg } from "../orgs/org-context";
import QRCode from "qrcode";
import { toast } from "react-toastify";

const TYPES = ["events", "games", "tracking", "access"];

const pageLayoutStyle = {
  display: 'flex',
  gap: '20px',
  alignItems: 'flex-start'
};

const leftColumnStyle = {
  flex: 1,
  minWidth: 0
};

const rightColumnStyle = {
  width: '320px',
  flexShrink: 0
};

const cardPanelStyle = {
  background: "#eef2f7",
  padding: "16px 20px"
};

const whiteInnerCardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16
};

export default function EditAction() {
  const { id } = useParams();
  const { activeOrgId } = useOrg();
  const [loadedOrgId, setLoadedOrgId] = useState(null);
  const [landingPagesAvailable, setLandingPagesAvailable] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [type, setType] = useState(TYPES[0]);
  const [activationRequested, setActivationRequested] = useState(false);
  const [status, setStatus] = useState("active");

  const [datasetId, setDatasetId] = useState("");
  const [datasetName, setDatasetName] = useState("");

  const [landingPageId, setLandingPageId] = useState("");
  const [cardDesignId, setCardDesignId] = useState("");

  const [landingPages, setLandingPages] = useState([]);
  const [cards, setCards] = useState([]);

  const [printerGroups, setPrinterGroups] = useState([]);
  const [printerGroupId, setPrinterGroupId] = useState("");

  const [groupPrinters, setGroupPrinters] = useState([]);
  const [selectedPrinters, setSelectedPrinters] = useState([]);
  const [printMode, setPrintMode] = useState("gallery");

  const [qrUrl, setQrUrl] = useState("");
  const [qrImg, setQrImg] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const printersDisabled = printMode === "none";
  const [landscapeCardDesignId, setLandscapeCardDesignId] = useState("");
  const [portraitCardDesignId, setPortraitCardDesignId] = useState("");
  const [copied, setCopied] = useState(false);

  const rowStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: "220px minmax(0,1fr)",
    gap: 12,
    alignItems: "center",
  }), []);

  const controlStyle = { width: "100%" };

  // QR
  useEffect(() => {
    const url = `${window.location.origin}/lp/${id}`;
    setQrUrl(url);
    QRCode.toDataURL(url, { width: 280 }).then(setQrImg);
  }, [id]);

  // Scope every request to the organization that opened this editor.
  useEffect(() => {
    let cancelled = false;
    const scopedFetch = async (url, options = {}) => {
      const result = await apiFetch(url, { ...options, orgIdOverride: activeOrgId });
      if (cancelled) throw new Error("Editor context changed");
      return result;
    };
    (async () => {
      try {
        setLoading(true);
        setLandingPagesAvailable(false);
        setLandingPages([]);
        setPrinterGroups([]);
        setLoadedOrgId(null);
        if (!activeOrgId) return;

        const act = await scopedFetch(`/actions/getById/${id}`);
        if (String(act.orgId) !== String(activeOrgId)) throw new Error("Action organization mismatch");
        setName(act.name || "");
        setType(act.type || TYPES[0]);
        setStatus(act.status || "inactive");

        setLandingPageId(act.landingPageId || "");
        setCardDesignId(act.cardDesignId || "");
        setLandscapeCardDesignId(
          act.landscapeCardDesignId || ""
        );
        setPortraitCardDesignId(
          act.portraitCardDesignId || ""
        );

        setPrinterGroupId(act.printerGroupId || "");
        setPrintMode(act.printMode || "gallery");

        const initialPrinters = act.printerNames || [];
        setSelectedPrinters(initialPrinters);

        const dsId = act.datasetId;
        setDatasetId(dsId);

        if (dsId) {
          const ds = await scopedFetch(`/datasets/get/${dsId}`);
          setDatasetName(ds?.name || dsId);
        }

        const groups = await scopedFetch("/api/printmanager/printergroups");
        setPrinterGroups(groups || []);

        // Landing Pages are optional here. Preserve the saved assignment on failure.
        try {
          const lp = await scopedFetch("/landingpages/getAll", { method: 'POST' });
          if (!Array.isArray(lp)) throw new Error("Invalid Landing Pages response");
          setLandingPages(lp);
          setLandingPagesAvailable(true);
        } catch (err) {
          if (cancelled) return;
        }

        const cd = await scopedFetch("/cards/getAll");
        setCards(cd.items || []);
        setLoadedOrgId(activeOrgId);

      } catch (err) {
        if (cancelled) return;
        toast.error("Failed to load Action");
        navigate("/datasets");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, activeOrgId, navigate]);

  // 🔥 Load printers when group changes
  useEffect(() => {
    let cancelled = false;
    setGroupPrinters([]);
    if (!printerGroupId) {
      setGroupPrinters([]);
      return;
    }

    (async () => {
      try {
        const res = await apiFetch(`/pm/printer-groups/${printerGroupId}/printers`, { orgIdOverride: activeOrgId });
        if (!cancelled) setGroupPrinters(res || []);
      } catch {
        if (!cancelled) toast.error("Failed to load printers in group");
      }
    })();
    return () => { cancelled = true; };
  }, [printerGroupId, activeOrgId]);

  const togglePrinter = (key) => {
    setSelectedPrinters((prev) =>
      prev.includes(key)
        ? prev.filter((p) => p !== key)
        : [...prev, key]
    );
  };

  const incomplete = incompleteAction({landingPageId, cardDesignId, landscapeCardDesignId, portraitCardDesignId, printMode, printerGroupId});

  const [testQr,setTestQr] = useState('');
  const testUrl=`${window.location.origin}/lp/${encodeURIComponent(id)}?test=1&orgId=${encodeURIComponent(activeOrgId || '')}`;
  useEffect(()=>{let current=true;QRCode.toDataURL(testUrl).then(url=>{if(current)setTestQr(url);});return()=>{current=false;};},[testUrl]);
  const handleSave = async () => {
    if (!activeOrgId || loadedOrgId !== activeOrgId || loading) return;
    try {
      setSaving(true);

      await apiFetch(`/actions/update/${id}`, {
        method: "POST",
        orgIdOverride: activeOrgId,
        body: {
          name,
          type,
          status: incomplete ? "inactive" : status,
          intent: activationRequested && !incomplete ? "activate" : "save",
          ...(landingPagesAvailable ? { landingPageId } : {}),
          cardDesignId,
          landscapeCardDesignId,
          portraitCardDesignId,
          datasetId,
          printerGroupId,
          printerNames: selectedPrinters,
          printMode,
        },
      });

      toast.success("Action updated");
      navigate(-1);

    } catch (error) {
      toast.error(error.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadedOrgId !== activeOrgId) return <div>Loading…</div>;



  return (
    <div style={{ marginLeft: 20 }}>

      <details><summary>Test on a phone</summary><p>Save the Landing Page assignment first. Sign in as an organization Owner/Admin on the test device.</p><a href={testUrl} target="_blank" rel="noreferrer">Open test Landing Page</a>{testQr && <img src={testQr} alt="Test Landing Page QR code" style={{display:'block',width:160,height:160}}/>}</details>
      {incomplete && <p>Incomplete configuration will be saved as Inactive / Draft. Save a Landing Page before activation. Printing actions also require a Card Design and Printer Group.</p>}
      <div className="datatable-header-row">
        <h2>Edit Action</h2>
        <div>
          <button className="btn-blue" disabled={!landingPageId} onClick={()=>window.open(testUrl,'_blank','noopener,noreferrer')}>Test Landing Page</button>&nbsp;
          <button className="btn-blue" onClick={() => navigate(-1)}>Return</button>&nbsp;&nbsp;&nbsp;
          <button className="btn-green" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div><br/>

      <div style={pageLayoutStyle}>

        {/* LEFT COLUMN */}

        <div style={leftColumnStyle}>

          <div
            className="detail-card"
            style={cardPanelStyle}
          >

            <div style={rowStyle}>
              <label><b>Project Name:</b> {datasetName}</label>
            </div>

            <br />

            <div style={rowStyle}>
              <label>Action Name</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div style={rowStyle}>
              <label>Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
              >
                {TYPES.map(t => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Landing Page</label>
              <select
                aria-label="Landing Page"
                disabled={!landingPagesAvailable}
                value={landingPageId}
                onChange={e => setLandingPageId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {landingPageId && !landingPages.some(lp => lp._id === landingPageId) && (
                  <option value={landingPageId}>Existing Landing Page (preserved)</option>
                )}
                {landingPages.map(lp => (
                  <option key={lp._id} value={lp._id}>
                    {lp.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              {!landingPagesAvailable && <div role="status">Landing Pages could not be loaded. The existing assignment will be preserved.</div>}
              <label>Landscape Card</label>

              <select
                value={landscapeCardDesignId}
                onChange={e => setLandscapeCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Portrait Card</label>

              <select
                value={portraitCardDesignId}
                onChange={e => setPortraitCardDesignId(e.target.value)}
              >
                <option value="">-- Select --</option>

                {cards.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={rowStyle}>
              <label>Status</label>

              <select
                value={incomplete ? "inactive" : status}
                onChange={e => { if (e.target.value === "active" && incomplete) { toast.error("Save a Landing Page before activation. Printing actions also require a Card Design and Printer Group."); return; } setStatus(e.target.value); setActivationRequested(e.target.value === "active"); }}
              >
                <option value="active">active</option>
                <option value="inactive">Inactive / Draft</option>
              </select>
            </div>

            <div style={{ opacity: printersDisabled ? 0.5 : 1 }}>
              <div style={rowStyle}>
                <label>Printer Group</label>

                <select
                  aria-label="Printer Group"
                  value={printerGroupId}
                  disabled={printersDisabled}
                  onChange={e => setPrinterGroupId(e.target.value)}
                >
                  <option value="">-- Select --</option>

                  {printerGroups.map(g => (
                    <option key={g._id} value={g._id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={rowStyle}>
              <label>Printers in Group</label>

              <div className="printer-checkbox-grid">

                {groupPrinters.length === 0 && (
                  <div style={{ color: "#666" }}>
                    No printers assigned to this group
                  </div>
                )}

                {groupPrinters.map(p => (
                  <label
                    key={p.printerKey}
                    className="printer-checkbox"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPrinters.includes(p.printerKey)}
                      onChange={() =>
                        togglePrinter(p.printerKey)
                      }
                    />

                    {p.name || p.printerKey}
                    <br />
                  </label>
                ))}

              </div>
            </div>

            <div style={rowStyle}>
              <label>Print Mode</label>

              <select
                value={printMode}
                onChange={(e) =>
                  setPrintMode(e.target.value)
                }
                style={{ width: "100%" }}
              >
                <option value="gallery">
                  Gallery (manual print)
                </option>

                <option value="immediate">
                  Immediate Print
                </option>

                <option value="none">
                  No Print
                </option>
              </select>
            </div>

          </div>

        </div>

        {/* RIGHT COLUMN */}

        <div style={rightColumnStyle}>

          <div
            className="detail-card"
            style={cardPanelStyle}
          >

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}
            >
            <h3 style={{ margin: 0 }}>
              Action URL
            </h3>

            <button
              className="btn-blue"
              style={{
                padding: '4px 10px',
                fontSize: '.8rem'
              }}
              onClick={() => {

                navigator.clipboard.writeText(qrUrl);

                setCopied(true);

                setTimeout(() => {
                  setCopied(false);
                }, 2000);

              }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>

            </div>

            <div style={whiteInnerCardStyle}>

              <div
                style={{
                  wordBreak: 'break-all',
                  fontSize: '.85rem',
                  marginBottom: '20px'
                }}
              >
                {qrUrl}
              </div>

              {qrImg && (
                <div style={{ textAlign: 'center' }}>
                <img
                  src={qrImg}
                  alt="QR"
                  style={{
                    width: '250px',
                    maxWidth: '100%'
                  }}
                />
                </div>
            )}
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}
